import { useState, useEffect } from 'react';

import type { IItemSelectorGroupProps } from 'libs/design-system/src/types/item_selector/item-selector-group';
import type { TItemSelector } from 'libs/design-system/src/types/item_selector/item-selector';

import { ItemSelector } from '../ItemSelector';
import { Label } from '../../Label';

import styles from './item-selector-group.module.scss';

export const ItemSelectorGroup = ({
  label,
  orientation = 'horizontal',
  type = 'checkbox',
  items,
  hasFirstItemSelected = false,
}: IItemSelectorGroupProps) => {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    const checkedItems = items
    .filter(item => (item as any).checked)
    .map(item => item.id);

    if (type === 'radio') {
      if (checkedItems.length === 0 && hasFirstItemSelected) {
        const firstEnabledItem = items.find(item => !item.disabled);
        if (firstEnabledItem) {
          setSelectedItems(new Set([firstEnabledItem.id]));
        }
      } else {
        setSelectedItems(new Set(checkedItems));
      }
    } else {
      setSelectedItems(new Set(checkedItems));
    }
  }, [items, type, hasFirstItemSelected]);

  const createItemClickHandler = (itemProps: TItemSelector) => {
    return (checked: boolean, value?: string) => {
      if (type === 'radio') {
        setSelectedItems(new Set([itemProps.id]));
      } else {
        const newSelectedItems = new Set(selectedItems);
        if (checked) {
          newSelectedItems.add(itemProps.id);
        } else {
          newSelectedItems.delete(itemProps.id);
        }
        setSelectedItems(newSelectedItems);
      }

      if (!hasFirstItemSelected) {
        itemProps?.onClickHandler?.(checked, value);
      }
    };
  };

  return (
    <div
      className={styles['item-selector-group']}
      data-item-selector-group-orientation={orientation}
    >
      {label && <Label text={label} />}
      
      <div className={styles['item-selector-group__items']}>
        {items.map((itemProps, idx) => (
          <ItemSelector 
            key={itemProps.id || idx} 
            type={type}
            selected={type === 'radio' ? selectedItems.has(itemProps.id) : undefined}
            checked={selectedItems.has(itemProps.id)}
            onClickHandler={createItemClickHandler(itemProps)}
            {...itemProps}
          />
        ))}
      </div>
    </div>
  );
};

export default ItemSelectorGroup;
